/* The gallery and the upload queue.
   ─────────────────────────────────────────────────────────────────────────────
   NOTE FOR WHOEVER PICKS THIS UP: the second seam. Everything a real
   `services/media` would own is behind this module — listing, cursor
   pagination, presigned PUTs, the processor's thumbnail callback. What runs here
   instead is a local store: fixtures for other guests, real File objects for
   yours, and a simulated transfer so the queue's honesty (progress, estimate,
   retry, cancel) can be built and judged. Blobs live in memory, so your own
   uploads do not survive a reload; nothing else about the model changes when the
   service arrives.

   Rules that are real and must stay real:
   · Every guest sees every upload (D35); you may edit and delete only your own,
     and ownership is the verified identity, not the browser (D44).
   · Video is capped at three minutes and checked BEFORE the transfer starts
     (D25) — never make a guest wait through an upload to be rejected at the end.
   · Videos never autoplay in the grid and are excluded from the slideshow (D26).
   · Admins can hide and unhide, reversibly, and cannot delete (D40).
   · Any guest may like any photo but their own; three likes feature it, and
     featuring latches — withdrawing a like never takes a photo off the wall
     (D52). Only an organiser can feature or unfeature by hand (D50). */

import { useEffect, useState } from 'react';
import {
  THUMB_LONG_EDGE_PX,
  type LikeResponse,
  type LikesResponse,
  type MediaItem as ServerMediaItem,
  type PhotosResponse,
} from '@mv/contracts';
import { ApiError, NotSignedIn, api } from './api';
import { needsMultipart, uploadMultipart, uploadSingle } from './transfer';
import { now } from './time';
import { getSession, type Suit } from './session';

export const MAX_VIDEO_SECONDS = 180;

export type MediaKind = 'photo' | 'video';
export type UploadState = 'uploading' | 'processing' | 'done' | 'failed' | 'rejected' | 'queued';

export interface MediaItem {
  id: string;
  kind: MediaKind;
  /** The display copy — 2560px, made in the browser. Never the camera original
   *  (D25 amendment). For fixtures this is a generated stand-in. */
  src: string;
  /** Grid thumbnail — the dominant bandwidth cost on the whole system.
   *  Empty string when a video's poster frame could not be grabbed (a codec
   *  the browser will not decode); the tile renders a blank frame with the
   *  play badge rather than a broken image. */
  thumb: string;
  durationSec?: number;
  uploaderId: string;
  uploaderName: string;
  uploaderTeam: Suit;
  caption?: string;
  eventId?: string;
  createdAt: number;
  hidden: boolean;
  /** On the wall and in the Featured tab (D51). Server-owned: three likes or an
   *  organiser set it, and it latches — unliking never clears it. */
  featured: boolean;
  /** Server-owned, and the one field a poll may legitimately disagree with a
   *  fresh local tap about. See `pendingLikes`. */
  likeCount: number;
  /** Absent when not featured. Never `'upload'` — nothing features on arrival,
   *  including an organiser's own uploads (D52). */
  featuredBy?: 'likes' | 'admin';
  /** An organiser took it off the wall, and further likes will not put it back
   *  (D50). The UI needs this to avoid promising a promotion that cannot
   *  happen — see `PhotoDetail`. */
  featureLocked?: boolean;
  state: UploadState;
  /** 0–1 while uploading. */
  progress?: number;
  /** Bytes per second, for the estimate. */
  fileName?: string;
  error?: string;
}

/* The gallery starts empty. It used to be seeded with eighteen invented
   uploads drawn as gradients, which was useful while the grid, the crossfade
   and the caption band were being built and is actively misleading now: the
   whole point of the next pass is watching real files go through the presign,
   the processor and the slideshow. Fake rows would sit in the middle of that
   looking exactly like successes. The empty states already exist for this
   (Gallery's EmptyFor, the kiosk hold frame) — they are now the first thing
   you see, which is correct. */

let items: MediaItem[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((fn) => fn());

/**
 * Which photos this guest has liked, and which likes are still in the air.
 *
 * Declared up here rather than with `toggleLike` because `absorb` has to know
 * about `pendingLikes` — see the note on it there. `likedIds` is replaced
 * rather than mutated so a component holding the old Set can tell it changed.
 */
let likedIds = new Set<string>();

/**
 * Unsettled like deltas, by photo id: `+1` for a tap whose request has not come
 * back yet, `-1` for an untap, and they compose if a guest taps twice.
 *
 * This exists because `absorb`'s rule is "server data wins over the local copy",
 * and `likeCount` is the one field where that rule is wrong for a moment. A
 * 20-second poll that overtakes an in-flight like returns the pre-write count,
 * and the number under the heart visibly counts back down. `absorb` therefore
 * re-applies whatever is still pending on top of what the server said.
 *
 * An entry exists only while it is non-zero — a tap and an untap that are both
 * outstanding cancel out and remove it — so the map is empty except in the few
 * hundred milliseconds around a tap. `bumpPending` is the only thing that
 * writes it, and holds that.
 */
const pendingLikes = new Map<string, number>();

/**
 * Which account `likedIds` belongs to, and a counter that invalidates replies
 * belonging to the previous one.
 *
 * `likesEpoch` is the part that is not obvious. A like request that was in the
 * air when the reception laptop changed hands would otherwise settle *after*
 * the switch and put the organiser's like into the next guest's `likedIds` —
 * where it renders as a filled heart on a photo they have never seen. Each
 * request captures the epoch it started in and drops its own reply if the
 * number has moved.
 */
let likesFor: string | null = null;
let likesEpoch = 0;

let loaded = false;
let loading: Promise<void> | null = null;
let paging: Promise<void> | null = null;

/**
 * PAGING, AND WHY IT IS THREE CURSORS RATHER THAN ONE.
 * ─────────────────────────────────────────────────────────────────────────────
 * The gallery is three server reads, not one (see `refreshMedia` below), so it
 * is also three independent positions. They advance at wildly different rates:
 * `all` is every photo of the week and will run to dozens of pages, `mine` is
 * one guest's own and usually finishes in one, `hidden` is normally empty. A
 * single shared cursor cannot describe that.
 *
 * The cursor is a DynamoDB `LastEvaluatedKey`, not an offset, which is the
 * property the whole design leans on: it names *the row after which to
 * continue*. New photos arriving at the head — which is the entire traffic
 * pattern of a wedding — do not shift it. An offset would have to be corrected
 * on every poll and would still skip or repeat rows under concurrent uploads.
 */
const PAGE_LIMIT = 100; // PAGE_MAX in services/media. Fewer round trips on hotel wifi.

/**
 * The FIRST read is smaller, and the reason is the size of a row rather than
 * the number of them.
 *
 * D49 signs every `src` and `thumb` on the way out, and a presigned S3 URL is
 * ~1.3 kB of signature and session token. So a photo weighs ~2.9 kB of JSON, of
 * which about 90% is credentials. A hundred of them is ~285 kB, and the first
 * load makes two such reads (three for an admin) — **over half a megabyte of
 * JSON before a single thumbnail begins downloading.** On the 260 kB/s link
 * §8 models for the resort that is more than two seconds of nothing.
 *
 * The gallery renders 24 tiles to begin with, so a hundred was four times what
 * the first screen could use. 48 is two screens: enough that the sentinel does
 * not fire instantly, half the bytes in front of first paint.
 *
 * Only the first read. Polls stay at `PAGE_LIMIT` because they are background
 * work, and because the head page is also the window `reconcile` speaks for —
 * shrinking every read would quietly shrink how far back an admin's hide
 * reaches on an already-open tab.
 *
 * **This is a workaround for the payload, not a fix.** Deleting D49's signing
 * when `WebStack` puts the derivatives behind CloudFront turns each URL into a
 * short path and takes a row from ~2.9 kB to ~250 bytes — 12x, which is worth
 * more than any page size.
 */
const FIRST_PAGE = 48;

/**
 * `featured` arrives here alongside `hidden` rather than replacing it, and the
 * overlap is deliberate: `hidden` is consumed by five call sites and typecheck
 * is a merge gate, so removing it and adding this in one change could not land
 * without the UI that uses it. The retirement is its own merge (D50).
 */
type ListKey = 'all' | 'mine' | 'hidden' | 'featured';

interface ListState {
  /** Where the next page continues from. `undefined` before the first read. */
  cursor?: string;
  /** The server answered without a cursor: there is nothing further back. */
  done: boolean;
  /**
   * `createdAt` of the oldest row in this list's most recent HEAD page, and the
   * thing that makes a poll able to notice a deletion. See `reconcile`.
   * `-Infinity` once the list is short enough to fit in one page, because then
   * the head page is the whole list.
   */
  headFloor: number;
}

const blank = (): ListState => ({ cursor: undefined, done: false, headFloor: Infinity });
const lists: Record<ListKey, ListState> = {
  all: blank(), mine: blank(), hidden: blank(), featured: blank(),
};

/**
 * Whose gallery is currently in memory.
 *
 * This module is a module-level cache and `signOut` does not empty it, which
 * did not matter while every refresh replaced `items` wholesale — the next
 * sign-in simply overwrote everything. It matters now: paging keeps rows the
 * next read is not entitled to reconcile away, so an admin signing out on the
 * reception laptop and a guest signing in after them would inherit the admin's
 * hidden rows, their cursors, and their place in the list.
 *
 * `visibleTo` would still hide those rows, so this is not a leak of anything on
 * screen — it is state belonging to one account being carried into another's
 * session, which is worth not having regardless of who can see it.
 */
let loadedFor: string | null = null;

function resetPaging(): void {
  lists.all = blank();
  lists.mine = blank();
  lists.hidden = blank();
  lists.featured = blank();
}

/**
 * The admin shelf is not merely empty for a guest — it is a 403.
 *
 * Featured is the opposite and is listed for everybody: it is a tab, not a
 * shelf (D51), and the API does not gate it. That is the whole reason the
 * admin branch here disappears when `hidden` does.
 */
function listsFor(viewer: { isAdmin: boolean } | null): ListKey[] {
  return viewer?.isAdmin
    ? ['all', 'mine', 'featured', 'hidden']
    : ['all', 'mine', 'featured'];
}

function pathFor(key: ListKey, cursor?: string, limit = PAGE_LIMIT): string {
  const q = new URLSearchParams({ limit: String(limit) });
  if (key === 'mine') q.set('mine', 'true');
  if (key === 'hidden') q.set('hidden', 'true');
  if (key === 'featured') q.set('featured', 'true');
  if (cursor) q.set('cursor', cursor);
  return `/photos?${q.toString()}`;
}

/**
 * Which lists a row we already hold *ought* to come back in, judged from the
 * copy in hand. Deliberately the local copy: the question being asked is "the
 * server did not return this — should it have?", so the fresh answer is exactly
 * the thing that is missing.
 */
function claims(it: MediaItem): Record<ListKey, boolean> {
  return {
    all: !it.hidden,
    mine: false, // never reconciled — see RECONCILABLE
    hidden: it.hidden,
    featured: it.featured, // also never reconciled — see RECONCILABLE
  };
}

/**
 * The only lists allowed to delete a row, and the restriction is the fix for a
 * bug that deleted eight live photographs in testing.
 *
 * `all` and `hidden` are the two halves of one DynamoDB index: GSI2 is
 * partitioned on `hidden`, so "would this row have been in that read" is the
 * single field `it.hidden`, and the client's answer cannot disagree with the
 * server's.
 *
 * `mine` is a different index (GSI1, on `uploaderId`) answering a different
 * question, and it returns hidden rows as well as visible ones — so the client
 * predicate `uploaderId === viewer.userId` is a *guess* at what that query
 * would return. When `mine` fits in one page it has no cursor, its floor drops
 * to `-Infinity`, and that guess is then handed authority over every row in the
 * store. Measured against 250 photographs: one poll removed eight of them,
 * permanently, with no error anywhere — they existed on the server the whole
 * time and simply stopped being on screen.
 *
 * `mine` still runs, still merges, and still protects its rows from being
 * dropped by the other two (everything it returns lands in `returned`). It just
 * does not get a vote on deletion.
 *
 * **`featured` is left out too, and not because it is unsafe.** It would
 * technically qualify: the client predicate `it.featured` matches the server's
 * `feat` key exactly, the same way `it.hidden` matches GSI2's partition. It is
 * excluded because it buys nothing the other two do not already cover, and the
 * only thing it could add is a second opinion on whether a row still exists —
 * which is precisely the shape of the bug above.
 *
 * Unfeaturing reaches other phones through the gallery read refreshing
 * `featured` on the row, not through the row being dropped. The row *should*
 * stay: it is still in the gallery. Only the Featured tab's predicate changes.
 *
 * NOTE FOR ANYONE TEMPTED TO ADD IT: no test fails if you do, and that is a
 * property of the current read shapes rather than a licence. `absorb` runs
 * before `reconcile`, so by the time `claims()` is consulted the row's
 * `featured` has already been refreshed from the gallery read — a row that
 * dropped out of the featured list no longer claims to be in it, and is never
 * judged. It becomes reachable the moment those two reads can disagree, which
 * is what the retirement merge changes. Left out on the argument above, not on
 * the test.
 */
const RECONCILABLE: ListKey[] = ['all', 'hidden'];

/**
 * A server row, with any like this browser has not had confirmed yet put back
 * on top of it.
 *
 * Clamped at zero: an untap that races the poll can otherwise read `-1` for an
 * instant, and "−1 likes" under a photograph is the kind of detail a guest
 * screenshots.
 */
function withPendingLike(row: MediaItem): MediaItem {
  const delta = pendingLikes.get(row.id);
  if (!delta) return row;
  return { ...row, likeCount: Math.max(0, row.likeCount + delta) };
}

/** Rows in, `items` out, merged by id. Server data wins over the local copy of
 *  the same row — except for an unsettled like, which is newer than the read
 *  that overtook it; anything still uploading from this browser is untouched. */
function absorb(rows: ServerMediaItem[]): void {
  const fresh = new Map(rows.map((r) => [r.id, withPendingLike({ ...r, state: 'done' as const })]));
  const merged = items.map((it) => {
    if (it.state !== 'done') return it; // in flight — not on the server yet
    const row = fresh.get(it.id);
    if (!row) return it;
    fresh.delete(it.id);
    return row;
  });
  items = [...merged, ...fresh.values()];
}

/**
 * Drop rows the server has stopped returning — which is the only way a hide or
 * a delete made on somebody else's phone ever reaches this one.
 *
 * A naive merge cannot do this. It only ever adds, so an admin hiding a photo
 * would leave it on every other guest's gallery and on the projector until they
 * reloaded, which is precisely the case moderation exists for.
 *
 * The rule: a row is dropped when some list we just re-read *should* have
 * contained it and did not. `headFloor` is what keeps that safe — the head page
 * only speaks for rows as old as its oldest row, so anything older than the
 * floor is beyond what this read saw and is left alone.
 *
 * KNOWN LIMIT, and it is a deliberate trade. Only the head window is reconciled
 * this way. A photo hidden after it has scrolled out of the newest hundred stays
 * visible on an already-open tab until it reloads. Reconciling the full depth
 * means refetching every page loaded so far on every 20-second poll, which grows
 * without bound over an evening and multiplies by every guest holding a phone.
 * In practice moderation lands on a photo somebody has just uploaded, which is
 * the window this does cover.
 */
function reconcile(fetched: ListKey[], returned: Set<string>): void {
  const judging = fetched.filter((k) => RECONCILABLE.includes(k));
  items = items.filter((it) => {
    if (it.state !== 'done') return true;
    if (returned.has(it.id)) return true;
    const claim = claims(it);
    return !judging.some((k) => claim[k] && it.createdAt >= lists[k].headFloor);
  });
}

/**
 * Pull the head of the gallery from the API.
 *
 * **Three reads, not one.** `/photos` with no parameters is the public gallery,
 * and the API answers it from the `hidden=false` partition of GSI2 — so a
 * hidden photo is not filtered out of that response, it was never in it. The
 * client used to make this one call and then slice the result three ways for
 * the All / Mine / Hidden tabs, which meant Hidden could only ever be empty and
 * Mine silently dropped the caller's own hidden uploads. Hiding a photo made it
 * vanish from every view including the one built to bring it back.
 *
 * So each tab gets the read that actually answers it:
 *
 *   `/photos`              the gallery, hidden excluded (everyone)
 *   `/photos?mine=true`    the caller's own, hidden included (everyone)
 *   `/photos?hidden=true`  the hidden shelf (admin only, enforced server-side)
 *
 * The admin read is skipped for a guest rather than attempted and swallowed: it
 * would 403, and a request whose failure is routine is a request not worth
 * making.
 *
 * **This reads the head only, and never moves a cursor.** Polling is how new
 * photos arrive, and new photos arrive at the front; a poll that also walked the
 * back pages would redo the whole evening's scrolling every twenty seconds.
 * `loadMoreMedia` owns the cursors, and the two do not interfere because a
 * `LastEvaluatedKey` is not disturbed by inserts ahead of it.
 *
 * Server rows replace server rows; anything still in flight from this browser
 * is kept, because it does not exist on the server yet and dropping it would
 * make a guest's own upload vanish from under them mid-transfer.
 */
export async function refreshMedia(): Promise<void> {
  // Read straight from the session rather than threading a viewer through the
  // seven call sites — polling, retries and the post-upload refresh all want
  // the same answer, and `session.ts` does not import this module, so there is
  // no cycle to worry about.
  const viewer = getSession();
  const keys = listsFor(viewer);

  // A different account than the rows in hand belong to: start clean rather
  // than merge two people's galleries.
  const who = viewer?.userId ?? null;
  if (who !== loadedFor) {
    items = items.filter((it) => it.state !== 'done'); // keep this browser's own uploads
    resetPaging();
    loadedFor = who;
    // Likes belong to the account, not to the browser. Clearing them here is
    // the same guarantee `resetPaging` gives the rows, and the epoch bump is
    // what stops a reply that is already in the air from putting them back.
    likedIds = new Set();
    pendingLikes.clear();
    likesFor = null;
    likesEpoch += 1;
  }

  const limit = loaded ? PAGE_LIMIT : FIRST_PAGE;

  /**
   * Likes are read once per account, not on every poll.
   *
   * `GET /likes/mine` is complete rather than paginated — it answers with every
   * id this guest has ever liked — so polling it re-sends the whole set every
   * twenty seconds to learn nothing, since the only thing that changes it is a
   * tap on this device. The cost of that choice is a like made on a second
   * phone not showing here until a reload, which is a fair trade for a set that
   * one person changes one entry at a time.
   *
   * A failure leaves `likesFor` unset so the next poll retries, and does not
   * take the gallery down with it: hearts that render empty are a smaller
   * problem than no photographs.
   */
  const wantLikes = who !== null && likesFor !== who;
  // Captured before the reads, and checked after them. Two polls can overlap,
  // and the account can change between this read starting and answering — the
  // same window `toggleLike` guards, reached from the other direction.
  const epoch = likesEpoch;
  const [pages, likes] = await Promise.all([
    Promise.all(keys.map((k) => api<PhotosResponse>(pathFor(k, undefined, limit)))),
    wantLikes ? api<LikesResponse>('/likes/mine').catch(() => null) : Promise.resolve(null),
  ]);

  if (likes && epoch === likesEpoch && likesFor !== who) {
    const settled = new Set(likes.photoIds);
    // A tap made while this read was in the air is newer than the read.
    for (const [id, delta] of pendingLikes) {
      if (delta > 0) settled.add(id);
      else if (delta < 0) settled.delete(id);
    }
    likedIds = settled;
    likesFor = who;
  }

  const returned = new Set<string>();
  keys.forEach((key, i) => {
    const page = pages[i];
    const state = lists[key];
    for (const row of page.items) returned.add(row.id);

    // A page that comes back without a cursor IS the whole list, so the floor
    // drops away and the reconcile below speaks for every row in it. With a
    // cursor, this read only saw as far back as its oldest row.
    state.headFloor = page.cursor && page.items.length > 0
      ? Math.min(...page.items.map((r) => r.createdAt))
      : -Infinity;

    // Only the first read of a list sets where paging starts. After that the
    // cursor belongs to `loadMoreMedia`, which may be many pages deeper.
    if (state.cursor === undefined && !state.done) {
      state.cursor = page.cursor;
      state.done = !page.cursor;
    }
  });

  absorb(pages.flatMap((p) => p.items));
  reconcile(keys, returned);

  loaded = true;
  emit();
}

/** Whether any list still has pages the server has not been asked for. */
export function hasMoreMedia(): boolean {
  return listsFor(getSession()).some((k) => !lists[k].done);
}

/**
 * Walk one page further back in every list that has one.
 *
 * All lists together rather than only the tab being looked at, because it is
 * cheap and the alternative is worse. `mine` and `hidden` exhaust after a page
 * or two and are then never requested again, so the cost is one extra small
 * read at the start and nothing after; whereas paging per tab means switching
 * from All to Mine lands on a half-loaded list with its own scroll position and
 * its own end-of-list state to get right.
 *
 * Concurrent calls collapse onto one promise. The sentinel that drives this can
 * fire several times while a page is in the air, and two overlapping reads from
 * the same cursor would fetch the same rows twice.
 */
export function loadMoreMedia(): Promise<void> {
  if (paging) return paging;
  const viewer = getSession();
  const keys = listsFor(viewer).filter((k) => !lists[k].done && lists[k].cursor);
  if (keys.length === 0) return Promise.resolve();

  paging = (async () => {
    const pages = await Promise.all(keys.map((k) => api<PhotosResponse>(pathFor(k, lists[k].cursor))));
    keys.forEach((key, i) => {
      const page = pages[i];
      lists[key].cursor = page.cursor;
      lists[key].done = !page.cursor;
    });
    // Appended, never reconciled: these rows are by definition older than every
    // head floor, so there is nothing here this read is entitled to judge.
    absorb(pages.flatMap((p) => p.items));
    emit();
  })().finally(() => { paging = null; });

  return paging;
}

/**
 * Load the entire gallery, page after page, and keep it loaded.
 *
 * For the projector only. `Display` shuffles the whole pool into a deck, so a
 * partial gallery is not a shorter scroll, it is photographs that never once go
 * on the screen all night — and nobody in the room can tell that from the deck
 * simply not having come round to theirs yet.
 *
 * Affordable in exactly the place it is used: one laptop on mains power, and
 * rows are metadata rather than pixels. The image bytes are fetched by the
 * `<img>` for the slide being shown, not by this.
 */
export async function loadAllMedia(): Promise<void> {
  // The head first, or there are no cursors to walk yet. `useMedia` normally
  // has this in flight already, so join that read rather than starting a second.
  if (loading) await loading.catch(() => {});
  if (!loaded) await refreshMedia();

  // Bounded so a paging bug cannot become an infinite request loop on a machine
  // nobody is watching. 200 pages is 20,000 photos; the wedding will not.
  for (let i = 0; i < 200 && hasMoreMedia(); i++) {
    const before = items.length;
    await loadMoreMedia();
    if (items.length === before) break; // no progress — stop rather than spin
  }
}

/**
 * Walk the featured list to its end, and nothing else.
 *
 * What `loadAllMedia` is for the gallery, this is for the projector — and it is
 * the reason the wall stops being expensive. D51 puts only featured photos on
 * the screen, so pulling every page of the week to show a few hundred of them
 * was six thousand signed rows for a list the kiosk then threw most of away. A
 * row is ~2.9 kB under D49's signing; this reads the sparse index instead.
 *
 * The gallery's own cursors are untouched: `all` and `mine` stay wherever the
 * scroll left them.
 */
export async function loadAllFeatured(): Promise<void> {
  if (loading) await loading.catch(() => {});
  if (!loaded) await refreshMedia();

  // Same bound and same reason as `loadAllMedia`: a paging bug on a machine
  // nobody is watching must cost 200 reads, not an evening of them.
  for (let i = 0; i < 200; i++) {
    // `loadMoreMedia` advances this cursor too. Waiting for it is cheaper than
    // both of them reading the same page.
    if (paging) await paging.catch(() => {});

    // Read fresh every lap rather than captured once. `resetPaging` REPLACES
    // these objects, so a walk holding a reference across a change of account
    // would keep advancing an orphan — pouring the previous viewer's pages into
    // the new one's gallery, from a cursor nothing else can see.
    const state = lists.featured;
    if (state.done || !state.cursor) break;

    const from = state.cursor;
    const page = await api<PhotosResponse>(pathFor('featured', from));

    // Whoever we were reading for is gone. These rows are theirs, not the
    // current viewer's, so they are dropped rather than merged.
    if (lists.featured !== state) break;

    absorb(page.items);
    emit();

    // Somebody moved the cursor while that page was in the air. Writing ours
    // over theirs would re-walk a page (harmless) or declare the list finished
    // early (not harmless), so hand over rather than overwrite.
    if (state.cursor !== from) break;
    state.cursor = page.cursor;
    state.done = !page.cursor;
  }
}

/** The projector's data hook: everything, once, then the ordinary poll keeps
 *  the head fresh. */
export function useAllMedia(): MediaItem[] {
  const media = useMedia();
  useEffect(() => {
    void loadAllMedia().catch(() => { /* Offline. The deck plays what arrived. */ });
  }, []);
  return media;
}

/**
 * The projector's data hook under D51: the featured pool loaded to its end,
 * then the ordinary poll keeps the head fresh.
 *
 * Returns the whole store rather than only the featured rows — `Display` does
 * its own filtering, and the gallery reads that came with the walk are already
 * paid for. What changes against `useAllMedia` is what gets *fetched*: one
 * sparse cursor instead of every page of the week.
 */
export function useAllFeatured(): MediaItem[] {
  const media = useMedia();
  useEffect(() => {
    void loadAllFeatured().catch(() => { /* Offline. The deck plays what arrived. */ });
  }, []);
  return media;
}

export function useMedia(): MediaItem[] {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    if (!loaded && !loading) {
      loading = refreshMedia()
        .catch(() => { /* Offline or signed out. The empty state says so. */ })
        .finally(() => { loading = null; });
    }
    return () => { listeners.delete(fn); };
  }, []);
  return items;
}

/** True once the first load has come back, so the gallery can tell "empty"
 *  apart from "not asked yet" and show a skeleton rather than "no photos". */
export const mediaLoaded = () => loaded;

/**
 * Poll for what other people have uploaded.
 *
 * The gallery and the projector both need photos that arrived from somebody
 * else's phone, and nothing pushes them here — there is no socket, by design
 * (D12/D17: this whole system is static files and stateless reads). A poll is
 * the honest shape for that.
 *
 * Skipped while the tab is hidden, so a phone in a pocket is not doing this all
 * night, and it fires once immediately on becoming visible again — a guest
 * coming back to the app should not wait an interval to see what they missed.
 */
export function useMediaPolling(everyMs: number): void {
  useEffect(() => {
    let cancelled = false;

    const pull = () => {
      if (cancelled || document.hidden) return;
      void refreshMedia().catch(() => { /* Offline. The last copy stays up. */ });
    };

    const id = setInterval(pull, everyMs);
    const onVisible = () => { if (!document.hidden) pull(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [everyMs]);
}

export function getItem(id: string): MediaItem | undefined {
  return items.find((it) => it.id === id);
}

/**
 * What a given viewer is allowed to see, newest first.
 *
 * A hidden photo is visible to an admin **or to the person who uploaded it**.
 * The second half was missing, and the API had it right all along: `getById`
 * already serves a hidden item to its owner, so the client was hiding
 * something the server would happily have returned by direct link. Hiding is a
 * moderation tool (D40) — it takes a photo off the shared wall, it does not
 * confiscate it from the guest who took it.
 */
export function visibleTo(all: MediaItem[], viewer: { userId: string; isAdmin: boolean } | null) {
  return all
    .filter((it) => (
      !it.hidden || Boolean(viewer?.isAdmin) || it.uploaderId === viewer?.userId
    ))
    .filter((it) => it.state !== 'rejected')
    .sort((a, b) => b.createdAt - a.createdAt);
}

function patch(id: string, changes: Partial<MediaItem>) {
  items = items.map((it) => (it.id === id ? { ...it, ...changes } : it));
  emit();
}

/* Mutations are optimistic: the local row changes at once and the request
   follows. A guest tapping hide on a photo that is on a projector in the next
   room should see it go immediately, and a failure is recoverable by trying
   again — whereas a spinner on every caption keystroke is not. */

export function setCaption(id: string, caption: string) {
  const next = caption.trim() || undefined;
  patch(id, { caption: next });
  void api(`/photos/${id}`, { method: 'PATCH', json: { caption: next ?? '' } })
    .catch(() => { void refreshMedia().catch(() => {}); });
}

export function setHidden(id: string, hidden: boolean) {
  patch(id, { hidden });
  void api(`/photos/${id}/visibility`, { method: 'POST', json: { hidden } })
    .catch(() => { void refreshMedia().catch(() => {}); });
}

/**
 * Put a photo on the wall, or take it off. Organisers only, enforced server
 * side (D50).
 *
 * The route answers `{ ok, changed }`, and `changed: false` is deliberately not
 * treated as a failure here: it means the photo was already in the state being
 * asked for, so the optimistic patch above is already right and there is
 * nothing to correct. It is worth something to a caller that wants to say
 * "already featured" — that belongs with the button, not with the store.
 *
 * `featuredBy` is set locally to `'admin'` because that is what this call makes
 * it; the next read replaces the row wholesale either way.
 */
export function setFeatured(id: string, featured: boolean) {
  patch(id, { featured, featuredBy: featured ? 'admin' : undefined });
  void api(`/photos/${id}/feature`, { method: 'POST', json: { featured } })
    .catch(() => { void refreshMedia().catch(() => {}); });
}

/**
 * Move a photo's pending delta by `delta`, and return where it ended up.
 *
 * The one invariant: **an entry exists only while it is non-zero.** A tap and
 * an untap that have both been sent but neither answered net to nothing, and
 * storing that as a literal `0` would work only because `withPendingLike`
 * happens to test `!delta` — swap that for an `undefined` check one day and a
 * dormant zero starts subtracting. Cheaper to not have the state than to
 * remember why it is harmless.
 */
function bumpPending(id: string, delta: number): number {
  const next = (pendingLikes.get(id) ?? 0) + delta;
  if (next === 0) pendingLikes.delete(id);
  else pendingLikes.set(id, next);
  return next;
}

/**
 * Retire this request's share of the pending delta and return what is left.
 *
 * Left, rather than zero, because a guest can tap twice before the first reply
 * lands. The second tap's `+1`/`-1` is still unsettled and still has to be
 * applied on top of whatever count the first reply carried.
 */
const dropPending = (id: string, delta: number): number => bumpPending(id, -delta);

/** Every photo this guest has liked. The hearts across the whole app render
 *  from this one Set rather than from a field on each row — see
 *  `LikesResponse` on why a `likedByMe` boolean would have been expensive. */
export function useLiked(): Set<string> {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return likedIds;
}

/**
 * Like a photo, or take a like back (D52).
 *
 * Optimistic like every other mutation here: the heart fills and the number
 * moves on the tap, and the request follows. What is different is that
 * `likeCount` is server-owned, so the optimism has to survive a poll landing in
 * the middle of it — `pendingLikes` is that, and `absorb` is where it is
 * re-applied.
 *
 * Refused before it is sent in the two cases the server would refuse anyway: a
 * row still uploading has no id on the server yet, and a *guest's* self-like is
 * a 400, because at a threshold of three a reflexive self-like would make the
 * real bar two (D52). An organiser's is not refused — see the guard.
 *
 * A failure is put back by arithmetic rather than by refetching. The photo may
 * be a thousand rows deep in an evening's scroll, and a head-page poll does not
 * reach that far — so a refresh would leave the count visibly wrong exactly
 * where it is hardest to notice.
 */
export function toggleLike(id: string): void {
  const viewer = getSession();
  if (!viewer) return;
  const item = items.find((it) => it.id === id);
  if (!item || item.state !== 'done') return;
  // A guest may not like their own photo and the API would refuse it, so the
  // request is not worth making. An organiser may: their like is the promotion
  // rather than a vote toward the threshold, and it is how they curate their
  // own uploads (D52 as amended). This guard must agree with `LikeButton`'s
  // `canAct` and with the route — three copies of one rule, and when they
  // disagree the UI offers a live control that silently does nothing.
  if (!viewer.isAdmin && item.uploaderId === viewer.userId) return;

  const liked = !likedIds.has(id);
  const delta = liked ? 1 : -1;
  const epoch = likesEpoch;

  const next = new Set(likedIds);
  if (liked) next.add(id);
  else next.delete(id);
  likedIds = next;
  bumpPending(id, delta);
  patch(id, { likeCount: Math.max(0, item.likeCount + delta) });

  void api<LikeResponse>(`/photos/${id}/like`, { method: 'POST', json: { liked } })
    .then((res) => {
      if (epoch !== likesEpoch) return; // a different account is signed in now
      const rest = dropPending(id, delta);
      /* Both numbers come from the server, and `featured` especially cannot be
         guessed here. Unfeaturing leaves a lock on the row that three further
         likes deliberately do not undo (D50), so a client that promoted the
         tile by arithmetic would be wrong and would stay wrong until the next
         poll — on the one screen where being wrong is most visible. */
      patch(id, { likeCount: Math.max(0, res.likeCount + rest), featured: res.featured });
    })
    .catch(() => {
      if (epoch !== likesEpoch) return;
      const rest = dropPending(id, delta);
      // Only when nothing else is pending: a later tap is a newer statement of
      // what this guest wants, and this failure does not overrule it.
      if (rest === 0) {
        const back = new Set(likedIds);
        if (liked) back.delete(id);
        else back.add(id);
        likedIds = back;
      }
      const current = items.find((it) => it.id === id);
      if (current) patch(id, { likeCount: Math.max(0, current.likeCount - delta) });
      else emit();
    });
}

/** Local only. Used by `cancel` and by the server-delete path below. */
function forget(id: string) {
  const item = items.find((it) => it.id === id);
  if (item?.src.startsWith('blob:')) URL.revokeObjectURL(item.src);
  if (item?.thumb.startsWith('blob:')) URL.revokeObjectURL(item.thumb);
  items = items.filter((it) => it.id !== id);
  emit();
}

export function remove(id: string) {
  const item = items.find((it) => it.id === id);
  forget(id);
  // A row that never reached the server has nothing to delete there.
  if (item?.state === 'done') {
    void api(`/photos/${id}`, { method: 'DELETE' })
      .catch(() => { void refreshMedia().catch(() => {}); });
  }
}

/** Cancel is not delete: it stops an upload that is still going and leaves the
 *  server alone, because nothing was ever written there. */
export function cancel(id: string) {
  aborters.get(id)?.abort();
  aborters.delete(id);
  forget(id);
}

/* ── uploading ─────────────────────────────────────────────────────────────*/

export class RejectedFile extends Error {
  constructor(public fileName: string, public reason: string) { super(reason); }
}

/** Reads duration from the file itself, before a byte is uploaded (D25). */
export function videoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(el.duration); };
    el.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable')); };
    el.src = url;
  });
}

/** Long edge of the copy guests are actually served. The reception projector is
 *  1080p, so 2560 is a downscale on the way to the screen with room to spare —
 *  the pixels below this are the ones nobody can see (D25 amendment). */
const DISPLAY_LONG_EDGE = 2560;
const THUMB_LONG_EDGE = 480;

/**
 * Encode an already-decoded bitmap down to `longEdge`.
 *
 * Split out from the decode deliberately. Decoding a 24-megapixel phone photo
 * is the expensive half and it used to happen once per output size — the
 * display copy and the thumbnail each decoded the same file from scratch.
 * Decode once, encode twice.
 *
 * `OffscreenCanvas.convertToBlob` keeps the encode off the main thread where
 * the browser has it (Chrome, Safari 16.4+). `canvas.toBlob` is the fallback
 * and janks; that is the price of an old browser, not of every browser.
 */
async function encodeFrom(bitmap: ImageBitmap, longEdge: number, quality: number): Promise<Blob | null> {
  // Never upscale. A source already inside the bound is left exactly as it is —
  // inflating it adds bytes and no detail.
  const scale = Math.min(1, longEdge / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));

  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, w, h);
    try {
      return await canvas.convertToBlob({ type: 'image/webp', quality });
    } catch {
      return null;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(bitmap, 0, 0, w, h);
  return await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/webp', quality);
  });
}

/**
 * Decode a file to a bitmap, or null if the browser cannot read it — desktop
 * Chrome cannot decode HEIC, and an iPhone photo AirDropped to a laptop arrives
 * as one.
 *
 * Null means "use the original untouched", which is never worse than what we
 * would have sent, only slower. It must never mean "fail the upload".
 */
async function decode(file: Blob): Promise<ImageBitmap | null> {
  try {
    return await createImageBitmap(file);
  } catch {
    return null;
  }
}

/**
 * The two artefacts an upload actually produces, both made here rather than
 * server-side (D25 amendment).
 *
 * `display` is what the gallery, photo detail and the projector are served.
 * `thumb` is the grid tile. The full-resolution original is not uploaded: at
 * ~4 MB it is sixteen seconds of hotel wifi per photo against four, and the
 * detail it carries is below what a 1080p screen can resolve.
 */
export async function derivativesFor(file: File): Promise<{ display: Blob; thumb: Blob | null }> {
  if (!file.type.startsWith('image/')) return { display: file, thumb: null };
  const bitmap = await decode(file);
  if (!bitmap) return { display: file, thumb: null };
  try {
    const alreadySmall = Math.max(bitmap.width, bitmap.height) <= DISPLAY_LONG_EDGE;
    const display = alreadySmall ? null : await encodeFrom(bitmap, DISPLAY_LONG_EDGE, 0.82);
    const thumb = await encodeFrom(bitmap, THUMB_LONG_EDGE, 0.7);
    return { display: display ?? file, thumb };
  } finally {
    bitmap.close();
  }
}

/**
 * A video's poster frame — both as an object URL for the tile and as a blob to
 * upload, because a video has no display copy until MediaConvert has run and
 * the poster is the only thing the gallery can show until then.
 *
 * Photos get theirs from `derivativesFor`, which has already decoded the file
 * and can encode both sizes from the one bitmap.
 */
interface Poster {
  url: string;
  blob: Blob | null;
  /** Source pixel dimensions of the clip. Sent on to the presigner, which
   *  signs them into the upload as S3 metadata so the transcoder can size its
   *  output without upscaling — see `media-processor`'s `outputSize`. This is
   *  the only place in the browser that ever learns them. */
  width?: number;
  height?: number;
}

async function posterFrame(url: string): Promise<Poster> {
  return new Promise((resolve) => {
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.muted = true;
    el.src = url;
    const nothing: Poster = { url: '', blob: null };

    el.onloadeddata = () => {
      try {
        el.currentTime = Math.min(0.5, el.duration / 2);
      } catch { resolve(nothing); }
    };
    el.onseeked = () => {
      const vw = el.videoWidth;
      const vh = el.videoHeight;
      if (!vw || !vh) return resolve(nothing);

      // Scale the LONG edge to 480, not the width. Pinning width meant a
      // portrait clip — which is most of them — produced a 480x853 poster:
      // nearly twice the pixels asked for, on the thumbnail, which is the
      // single largest bandwidth cost in the system.
      const scale = Math.min(1, THUMB_LONG_EDGE_PX / Math.max(vw, vh));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(vw * scale);
      canvas.height = Math.round(vh * scale);

      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve(nothing);
      ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
      // WebP to match what the thumb target's presign policy expects.
      canvas.toBlob(
        (blob) => resolve(blob
          ? { url: URL.createObjectURL(blob), blob, width: vw, height: vh }
          : nothing),
        'image/webp',
        0.7,
      );
    };
    el.onerror = () => resolve(nothing);
  });
}

export interface Uploader { userId: string; name: string; team: Suit }

/**
 * Adds files to the queue. Anything unacceptable is turned away here, with a
 * sentence, rather than after a three-minute wait.
 */
export async function enqueue(files: File[], uploader: Uploader, caption?: string, eventId?: string) {
  const posterWork: (() => Promise<void>)[] = [];

  for (const file of files) {
    const id = `up-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (!isVideo && !isImage) {
      items = [{
        id, kind: 'photo', src: '', thumb: '', uploaderId: uploader.userId,
        uploaderName: uploader.name, uploaderTeam: uploader.team, createdAt: now(),
        hidden: false, featured: false, likeCount: 0,
        state: 'rejected', fileName: file.name,
        error: 'That is not a photo or a video, so there is nothing to add.',
      }, ...items];
      emit();
      continue;
    }

    const url = URL.createObjectURL(file);
    let durationSec: number | undefined;

    if (isVideo) {
      try {
        durationSec = await videoDuration(file);
      } catch {
        durationSec = undefined;
      }
      if (durationSec && durationSec > MAX_VIDEO_SECONDS) {
        URL.revokeObjectURL(url);
        items = [{
          id, kind: 'video', src: '', thumb: '', durationSec,
          uploaderId: uploader.userId, uploaderName: uploader.name, uploaderTeam: uploader.team,
          createdAt: now(), hidden: false, featured: false, likeCount: 0,
          state: 'rejected', fileName: file.name,
          error: `That one is ${formatDuration(durationSec)} long. Videos have to be under three minutes — nothing was uploaded.`,
        }, ...items];
        emit();
        continue;
      }
    }

    // The row appears NOW, with no poster. Generating it first meant a decode
    // of every earlier file had to finish before the tenth even showed up in
    // the queue — pick twenty photos and the last one was invisible for the
    // best part of a minute, which reads exactly like a dropped upload.
    items = [{
      id, kind: isVideo ? 'video' : 'photo', src: url, thumb: '', durationSec,
      uploaderId: uploader.userId, uploaderName: uploader.name, uploaderTeam: uploader.team,
      caption, eventId, createdAt: now(), hidden: false,
      featured: false, likeCount: 0,
      state: 'queued', progress: 0, fileName: file.name,
    }, ...items];
    emit();

    posterWork.push(async () => {
      if (isVideo) {
        const poster = await posterFrame(url);
        if (!items.some((it) => it.id === id)) return;
        if (poster.url) patch(id, { thumb: poster.url });
        // The clip itself is what goes up; there is no browser-side transcode.
        // Above the multipart threshold `run` takes the resumable path.
        pending.set(id, {
          kind: 'video', contentType: file.type, primary: file,
          thumb: poster.blob, durationSec, caption, eventId,
          width: poster.width, height: poster.height,
        });
        void run(id);
        return;
      }

      // Derivatives before anything is sent. The camera original never leaves
      // the phone (D25): on this photo set that is a 21 MB file becoming a
      // 0.8 MB upload — twenty-eight times less to push over hotel wifi.
      const { display, thumb } = await derivativesFor(file);
      if (!items.some((it) => it.id === id)) return;
      patch(id, { thumb: thumb ? URL.createObjectURL(thumb) : url });
      pending.set(id, {
        kind: 'photo',
        // What is actually being sent — WebP normally, the untouched file when
        // the browser could not decode it (HEIC on desktop Chrome). Telling
        // the presigner the wrong type fails the POST policy's Content-Type
        // condition, not the transfer, so it must match the blob exactly.
        contentType: display === file ? file.type : 'image/webp',
        primary: display,
        thumb,
        caption,
        eventId,
      });
      void run(id);
    });
  }

  await pooled(posterWork, MAX_CONCURRENT_DECODES);
}

/**
 * Run tasks `limit` at a time. Decoding is CPU-bound, so more workers than this
 * does not go faster — it just competes with the render thread for the same
 * cores and makes the queue stutter while it scrolls.
 */
async function pooled(tasks: (() => Promise<void>)[], limit: number): Promise<void> {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    for (let i = next++; i < tasks.length; i = next++) await tasks[i]!();
  });
  await Promise.all(workers);
}

const running = new Set<string>();

/** Live uploads, so cancel can actually stop one mid-flight rather than just
 *  hiding the row while the bytes keep going. */
const aborters = new Map<string, AbortController>();

/**
 * How many uploads are in flight at once.
 *
 * On a bandwidth-bound link this does not make the total faster — measured, 20
 * files take the same wall-clock at 1, 3 or 6. What it buys is the per-file
 * overhead: a presign round-trip and a TLS handshake each, most of a second on
 * a resort connection, previously all in series. And one stalled transfer no
 * longer holds up the nineteen behind it.
 */
const MAX_CONCURRENT_TRANSFERS = 3;

/** Decodes are CPU-bound; a separate budget from the network one. */
const MAX_CONCURRENT_DECODES = 2;

let activeTransfers = 0;
const waitingForSlot: (() => void)[] = [];

function acquireSlot(): Promise<void> {
  if (activeTransfers < MAX_CONCURRENT_TRANSFERS) {
    activeTransfers += 1;
    return Promise.resolve();
  }
  // Queued. The releasing transfer hands its slot straight over rather than
  // decrementing, so the count cannot drift.
  return new Promise<void>((resolve) => waitingForSlot.push(resolve));
}

function releaseSlot(): void {
  const next = waitingForSlot.shift();
  if (next) next();
  else activeTransfers -= 1;
}

interface Pending {
  kind: MediaKind;
  contentType: string;
  primary: Blob;
  thumb: Blob | null;
  durationSec?: number;
  /** Video only. Source dimensions, on their way to becoming S3 metadata. */
  width?: number;
  height?: number;
  caption?: string;
  eventId?: string;
}

/** What each queued row still needs in order to be sent, kept out of the item
 *  itself so blobs are not handed to every component that renders a tile. */
const pending = new Map<string, Pending>();

async function run(localId: string) {
  if (running.has(localId)) return;
  running.add(localId);

  await acquireSlot();

  const work = pending.get(localId);
  if (!work || !items.some((it) => it.id === localId)) {
    running.delete(localId);
    releaseSlot();
    return;
  }

  const controller = new AbortController();
  aborters.set(localId, controller);
  patch(localId, { state: 'uploading', progress: 0, error: undefined });

  try {
    const onProgress = (f: number) => patch(localId, { progress: f });

    const photoId = needsMultipart(work.primary.size)
      ? await uploadMultipart({
        file: work.primary,
        contentType: work.contentType,
        durationSec: work.durationSec,
        width: work.width,
        height: work.height,
        thumb: work.thumb,
        onProgress,
        signal: controller.signal,
      })
      : await uploadSingle({
        kind: work.kind,
        contentType: work.contentType,
        primary: work.primary,
        thumb: work.thumb,
        durationSec: work.durationSec,
        width: work.width,
        height: work.height,
        onProgress,
        signal: controller.signal,
      });

    // The bytes are in S3 but the row does not exist until this returns —
    // deliberately, so an upload that dies halfway leaves no half-photo in the
    // gallery for anyone to explain.
    patch(localId, { state: 'processing', progress: 1 });
    await api('/photos/complete', {
      method: 'POST',
      json: {
        photoId,
        kind: work.kind,
        durationSec: work.durationSec,
        caption: work.caption,
        eventId: work.eventId,
      },
    });

    patch(localId, { state: 'done', progress: 1 });
    pending.delete(localId);

    // Pick up the server's own copy — its id, its attribution, and the URL the
    // gallery will actually serve rather than this browser's blob.
    void refreshMedia().catch(() => {});
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Cancelled. `cancel` has already removed the row.
    } else if (err instanceof NotSignedIn) {
      patch(localId, { state: 'failed', error: 'Your sign-in ran out. Sign in again and this will go up.' });
    } else if (!navigator.onLine) {
      patch(localId, { state: 'failed', error: "The wifi dropped out. Try again when it's back." });
    } else {
      patch(localId, {
        state: 'failed',
        error: err instanceof ApiError ? err.message : 'That did not go up. Try again.',
      });
    }
  } finally {
    aborters.delete(localId);
    releaseSlot();
    running.delete(localId);
  }
}

export function retry(id: string) {
  if (!pending.has(id)) return;
  patch(id, { state: 'queued', progress: 0, error: undefined });
  void run(id);
}


/** Anything the wifi dropped picks itself back up (principle 5: be trustworthy). */
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    items.filter((it) => it.state === 'failed').forEach((it) => retry(it.id));
  });
}

export function formatDuration(seconds: number): string {
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function estimateLabel(item: MediaItem, bytesPerSecond = 260_000): string {
  if (item.state !== 'uploading' || item.progress === undefined) return '';
  const remaining = (1 - item.progress) * (1_600_000 / bytesPerSecond);
  if (remaining < 5) return 'a few seconds left';
  if (remaining < 90) return `${Math.round(remaining)} seconds left`;
  return `about ${Math.round(remaining / 60)} minutes left`;
}
